import type { UiAccess } from "../lib/access";
import { CARD_REGISTRY } from "./cardRegistry";
import { TOPICS } from "./topics";

export type UiOutlineItem = {
  id: string;
  label: string;
  description?: string;
  access: UiAccess;
};

export type UiOutlineScreen = {
  id: string;
  label: string;
  path: string;
  access: UiAccess;
  cards: UiOutlineItem[];
};

export type UiOutlineSection = {
  id: string;
  label: string;
  items: UiOutlineItem[];
};

const CARD_LABELS: Record<string, string> = {
  "freq-hero": "Mains frequency hero chart with FOP change markers",
  "ws-chart-dash": "WS connections (ocpp-edge)",
  "chargers-dash": "Connected chargers",
  "ocpp-ws-hero": "WS connections (ocpp-edge)",
  "ocpp-traffic": "OCPP messages & Ethernet traffic",
  "chargers-ocpp": "Connected chargers",
  "freq-hero-topic": "Control frequency (hero chart)",
  "regional-meters": "Electrically Coherent Grid — regional meter trends",
  "charger-power": "Total charger power (kW)",
  "infra-perf": "Infrastructure & performance monitor",
  "admin-users": "Registered users",
  "devops-main": "Devops maintenance",
  "fcr-core-modules": "fcr-core modules, allocated-charger file, sample log",
};

function cardLabel(id: string, rowLabel?: string): string {
  return CARD_LABELS[id] ?? rowLabel ?? id;
}

/** Main nav screens and their cards — source of truth for the settings outline tab. */
export function buildUiOutline(): UiOutlineScreen[] {
  return TOPICS.map((topic) => {
    const cards = CARD_REGISTRY.filter((c) => c.topic === topic.id)
      .sort((a, b) => a.order - b.order)
      .map((card) => ({
        id: card.id,
        label: cardLabel(card.id, card.rowLabel),
        description: card.type === "placeholder" ? "Placeholder — not live yet" : undefined,
        access:
          topic.id === "admin" || topic.id === "devops"
            ? ("admin" as UiAccess)
            : ("all" as UiAccess),
      }));

    return {
      id: topic.id,
      label: topic.label,
      path: topic.path,
      access: topic.id === "admin" || topic.id === "devops" ? "admin" : "all",
      cards,
    };
  });
}

/** Side panels and nav actions outside the main content area. */
export const UI_PANEL_OUTLINE: UiOutlineSection[] = [
  {
    id: "panels",
    label: "Side panels",
    items: [
      {
        id: "live-data-mqtt",
        label: "Live data — MQTT live viewer",
        description: "Browse catalog topics and sniff live MQTT payloads",
        access: "all",
      },
      {
        id: "live-data-reference",
        label: "Live data — MQTT reference",
        description: "Topic schema from the catalog",
        access: "all",
      },
      {
        id: "live-data-sync",
        label: "Live data — Sync catalog from GitHub",
        access: "admin",
      },
      {
        id: "settings-outline",
        label: "Settings — UI outline",
        description: "Screen map and role visibility",
        access: "all",
      },
      {
        id: "users-panel",
        label: "Users panel",
        description: "Registered accounts and roles",
        access: "admin",
      },
    ],
  },
  {
    id: "sidebar",
    label: "Sidebar (context)",
    items: [
      {
        id: "sidebar-status",
        label: "Connection status & backend version",
        access: "all",
      },
      {
        id: "sidebar-sparkline",
        label: "A/B/C meter sparkline — C backup, dashed (dashboard & frequency)",
        access: "all",
      },
      {
        id: "sidebar-ocpp",
        label: "OCPP stats (connections, msg/s)",
        access: "all",
      },
      {
        id: "sidebar-infra",
        label: "Infra health summary",
        access: "all",
      },
    ],
  },
  {
    id: "nav-rail",
    label: "Nav rail",
    items: [
      { id: "nav-live-data", label: "Live data", description: "MQTT live viewer & reference", access: "all" },
      { id: "nav-devops", label: "Devops", description: "Admin maintenance (MQTT retain, restarts)", access: "admin" },
      { id: "nav-connections", label: "Connections", description: "Disabled in UI", access: "admin" },
      { id: "nav-alerts", label: "Alerts", description: "Disabled in UI", access: "admin" },
    ],
  },
];

export const ROLE_DESCRIPTIONS = [
  {
    role: "user" as const,
    summary: "Default for registered company users. Can view all main dashboards, charts, infra, and MQTT settings.",
  },
  {
    role: "demo" as const,
    summary: "Same view access as user — intended for demo or preview accounts.",
  },
  {
    role: "admin" as const,
    summary: "Full access including user management, admin screen, and catalog sync.",
  },
];
